import {
  Col,
  DeleteButton,
  EditButton,
  Form,
  getDefaultSortOrder,
  Icons,
  Input,
  List,
  Row,
  Space,
  Table,
  TagField,
  TextField,
  useTable,
  Slider,
} from "@pankod/refine-antd";
import {
  CrudFilters,
  IResourceComponentsProps,
  useSelect,
} from "@pankod/refine-core";
import type { CheckboxValueType } from "antd/es/checkbox/Group";
import type { ColumnsType } from "antd/es/table";
import { ButtonColumn } from "components/buttonColumn";
import { ButtonFilter } from "components/buttonFilter";
import React, { useState } from "react";
import { ITag } from "types/tag.interface";
import { ITrack, ITrackResponse } from "types/track.interface";
import {
  formatDateTime,
  formatTime,
  getOrderNumber,
  includesClassName,
} from "utils";
import { Select } from "antd";
import { IUser } from "types/user.interface";
import { IGenre } from "types/genre.interface";
import useMediaScreen from "hooks/useMediaScreen";
import RouterProvider from "@pankod/refine-react-router-v6";
export const TrackList: React.FC<IResourceComponentsProps> = () => {
  const { Link } = RouterProvider;

  const { options: labels } = useSelect<any>({
    resource: "labels",
    fetchSize: 100,
    optionLabel: "name",
    optionValue: "_id",
  });
  const { isMobile } = useMediaScreen();

  const { options: contributors } = useSelect<IUser>({
    resource: "users/contributor",
    optionLabel: "username",
    optionValue: "_id",
  });

  const { options: genres } = useSelect<IGenre>({
    resource: "genres",
    fetchSize: 1000,
    optionLabel: "name",
    optionValue: "_id",
  });

  const { options: tags } = useSelect<ITag>({
    resource: "tags",
    fetchSize: 1000,
    optionLabel: "name",
    optionValue: "_id",
  });

  const { tableProps, sorter, searchFormProps }: any = useTable<ITrackResponse>(
    {
      resource: "tracks",
      initialPageSize: 100,
      metaData: [
        {
          field: "isDraft",
          value: false,
        },
      ],
      onSearch: (params: any) => {
        const filters: CrudFilters = [];
        const input = params;
        filters.push({
          field: "search",
          operator: "in",
          value: {
            search: input.search || "",
            labelId: input.labelId || "",
            contributors: input?.contributors,
            tags: input?.tags,
            genres: input?.genres,
            bpm: input?.bpm,
            year: input?.year,
          },
        });

        return filters;
      },
    }
  );

  const current = tableProps?.pagination?.current;
  const pageSize = tableProps?.pagination?.pageSize;

  const defaultColumn: CheckboxValueType[] = [
    "title",
    "publishDate",
    "artist",
    "user",
    "price",
    "totalBuys",
    "createdAt",
  ];

  const [hideCols, setHideCols] = useState<CheckboxValueType[]>(defaultColumn);

  const options = [
    { label: "Title", value: "title" },
    { label: "Publish", value: "publishDate" },
    { label: "Artist", value: "artist" },
    { label: "Owner", value: "user" },
    { label: "Price", value: "price" },
    { label: "Total Downloads", value: "totalBuys" },
    { label: "Created", value: "createdAt" },
    { label: "Duration", value: "duration" },
    { label: "Genre", value: "genre" },
    { label: "Label", value: "label" },
    { label: "Tags", value: "tags" },
    { label: "TrackKey", value: "trackKey" },
    { label: "Type", value: "type" },
    { label: "Year", value: "year" },
  ];

  const columns: ColumnsType<ITrack> = [
    {
      title: "Index",
      dataIndex: "_id",
      key: "_id",
      render: (value, item, index) => (
        <TextField value={getOrderNumber(current, pageSize, index)} />
      ),
    },
    {
      title: "Publish Date",
      dataIndex: "publishDate",
      key: "publishDate",
      render: (value) => <TextField value={formatDateTime(value)} />,
      className: includesClassName(hideCols, "publishDate"),
      defaultSortOrder: getDefaultSortOrder("publishDate", sorter),
      sorter,
    },
    {
      title: "Title",
      dataIndex: "title",
      key: "title",
      render: (value) => <TextField value={value} />,
      className: includesClassName(hideCols, "title"),
      defaultSortOrder: getDefaultSortOrder("title", sorter),
      sorter,
    },
    {
      title: "Owner",
      dataIndex: "user",
      key: "user",
      render: (value) => <TextField value={value?.username} />,
      className: includesClassName(hideCols, "user"),
      defaultSortOrder: getDefaultSortOrder("user", sorter),
      sorter,
    },
    {
      title: "Artist",
      dataIndex: "artist",
      key: "artist",
      render: (value) => <TextField value={value} />,
      className: includesClassName(hideCols, "artist"),
      defaultSortOrder: getDefaultSortOrder("artist", sorter),
      sorter,
    },
    {
      title: "Total Downloads",
      dataIndex: "totalBuys",
      key: "totalBuys",
      render: (value) => (
        <Link to={`/track-purchases`}>
          <TextField value={value} />
        </Link>
      ),
      className: includesClassName(hideCols, "totalBuys"),
      defaultSortOrder: getDefaultSortOrder("totalBuys", sorter),
      sorter,
    },
    {
      title: "Price",
      dataIndex: "price",
      key: "price",
      render: (value) => <TextField value={value} />,
      className: includesClassName(hideCols, "price"),
      defaultSortOrder: getDefaultSortOrder("price", sorter),
      sorter,
    },
    {
      title: "CreatedAt",
      dataIndex: "createdAt",
      key: "createdAt",
      render: (value) => <TextField value={formatDateTime(value)} />,
      className: includesClassName(hideCols, "createdAt"),
      defaultSortOrder: getDefaultSortOrder("createdAt", sorter),
      sorter,
    },
    {
      title: "Duration",
      dataIndex: "duration",
      key: "duration",
      render: (value) => <TextField value={formatTime(value || 0)} />,
      className: includesClassName(hideCols, "duration"),
      defaultSortOrder: getDefaultSortOrder("duration", sorter),
      sorter,
    },
    {
      title: "Genre",
      dataIndex: "genre",
      key: "genre",
      render: (value) => <TextField value={value?.name} />,
      className: includesClassName(hideCols, "genre"),
      defaultSortOrder: getDefaultSortOrder("genre", sorter),
      sorter,
    },
    {
      title: "Label",
      dataIndex: "label",
      key: "label",
      render: (value) => <TextField value={value?.name} />,
      className: includesClassName(hideCols, "label"),
      defaultSortOrder: getDefaultSortOrder("label", sorter),
      sorter,
    },
    {
      title: "tags",
      dataIndex: "tags",
      key: "tags",
      render: (value) => (
        <div>
          {value.map((item: ITag) => (
            <TagField value={item?.name} />
          ))}
        </div>
      ),
      className: includesClassName(hideCols, "tags"),
      defaultSortOrder: getDefaultSortOrder("tags", sorter),
      sorter,
    },

    {
      title: "TrackKey",
      dataIndex: "trackKey",
      key: "trackKey",
      render: (value) => <TextField value={value?.musicKey} />,
      className: includesClassName(hideCols, "trackKey"),
      defaultSortOrder: getDefaultSortOrder("trackKey", sorter),
      sorter,
    },
    {
      title: "Type",
      dataIndex: "type",
      key: "type",
      render: (value) => <TextField value={value} />,
      className: includesClassName(hideCols, "type"),
      defaultSortOrder: getDefaultSortOrder("type", sorter),
      sorter,
    },
    {
      title: "Year",
      dataIndex: "year",
      key: "year",
      render: (value) => <TextField value={value} />,
      className: includesClassName(hideCols, "year"),
      defaultSortOrder: getDefaultSortOrder("year", sorter),
      sorter,
    },
    {
      title: "Action",
      dataIndex: "action",
      key: "action",
      render: (_, record) => (
        <Space>
          <EditButton hideText size="small" recordItemId={record._id} />
          <DeleteButton hideText size="small" recordItemId={record._id} />
        </Space>
      ),
    },
  ];

  return (
    <List>
      <Row style={{ display: "block" }}>
        <div style={{ display: "flex", gap: "5px", marginBottom: "5px" }}>
          <TextField
            value="Total:"
            style={{ color: "#626262", fontSize: "14px", fontWeight: 700 }}
          />
          <TextField
            value={tableProps?.pagination?.total}
            style={{ color: "#626262", fontSize: "14px", fontWeight: 700 }}
          />
        </div>
        <Col>
          <Form layout="vertical" {...searchFormProps}>
            <div
              style={{
                display: "flex",
                flexDirection: isMobile ? "column" : "row",
              }}
            >
              <Form.Item
                label="Search"
                name="search"
                style={{
                  width: isMobile ? "100%" : "30%",
                  marginRight: "15px",
                }}
                initialValue=""
              >
                <Input
                  placeholder="Title, artist"
                  prefix={<Icons.SearchOutlined />}
                />
              </Form.Item>
              <Form.Item
                label="label"
                name="labelId"
                style={{ width: isMobile ? "100%" : "30%" }}
              >
                <Select
                  style={{ width: "100%" }}
                  defaultValue={{ value: "", label: "All label" }}
                  options={labels}
                />
              </Form.Item>
              <Form.Item
                label="Contributors"
                style={{
                  width: isMobile ? "100%" : "30%",
                  marginLeft: isMobile ? "0" : "15px",
                }}
                name="contributors"
              >
                <Select
                  placeholder="Select contributors"
                  // mode='multiple'
                  showSearch
                  filterOption={(input, option: any) =>
                    option.label.toLowerCase().includes(input.toLowerCase())
                  }
                  options={contributors}
                />
              </Form.Item>
            </div>
            <div
              style={{
                display: "flex",
                flexDirection: isMobile ? "column" : "row",
              }}
            >
              <Form.Item
                label="Genres"
                style={{
                  width: isMobile ? "100%" : "50%",
                  marginRight: "15px",
                }}
                name="genres"
              >
                <Select
                  placeholder="Select genres"
                  mode="multiple"
                  options={genres}
                />
              </Form.Item>
              <Form.Item
                label="Tags"
                style={{
                  width: isMobile ? "100%" : "50%",
                  marginRight: "15px",
                }}
                name="tags"
              >
                <Select
                  placeholder="Select tags"
                  mode="multiple"
                  options={tags}
                />
              </Form.Item>
            </div>
            <div
              style={{
                display: "flex",
                flexDirection: isMobile ? "column" : "row",
              }}
            >
              <Form.Item
                label="BPM"
                style={{
                  width: isMobile ? "100%" : "50%",
                  marginRight: "40px",
                }}
                name="bpm"
              >
                <Slider
                  tooltipPlacement="bottom"
                  marks={{
                    0: "0",
                    220: 220,
                  }}
                  step={1}
                  min={0}
                  max={220}
                  range
                  defaultValue={[0, 220]}
                />
              </Form.Item>
              <Form.Item
                label="Year"
                style={{
                  width: isMobile ? "100%" : "50%",
                  marginRight: "15px",
                }}
                name="year"
              >
                <Slider
                  tooltipPlacement="bottom"
                  marks={{
                    1950: "1950",
                    [new Date().getFullYear()]: new Date().getFullYear(),
                  }}
                  step={1}
                  min={1950}
                  max={new Date().getFullYear()}
                  range
                  defaultValue={[1950, new Date().getFullYear()]}
                />
              </Form.Item>
            </div>

            <div style={{ display: "flex", justifyContent: "space-between" }}>
              <Form.Item>
                <ButtonFilter />
              </Form.Item>
              <div>
                <ButtonColumn
                  options={options}
                  setHideCols={setHideCols}
                  defaultColumn={defaultColumn}
                />
                {/* <ButtonExport path='tracks' /> */}
              </div>
            </div>
          </Form>
        </Col>
        <Col>
          <Table
            {...tableProps}
            pagination={{
              ...tableProps?.pagination,
              showSizeChanger: true,
            }}
            columns={columns}
            rowKey="id"
          />
        </Col>
      </Row>
    </List>
  );
};
